import type {
	query_rootRequest,
	query_root,
	mutation_rootRequest,
	mutation_root,
	subscription_rootRequest,
	subscription_root
} from '../generated/schema.js';
import {
	generateQueryOp,
	generateMutationOp,
	generateSubscriptionOp
} from '../generated/index.js';

type SecondElement<Tuple> = Tuple extends [any, infer Second] ? Second : never;
type FirstElement<Tuple> = Tuple extends [infer First, ...any[]] ? First : never;

export type QueryArguments<T extends keyof query_rootRequest> = SecondElement<
	NonNullable<query_rootRequest[T]>
>;
export type QueryOptions<T extends keyof query_rootRequest> = FirstElement<
	NonNullable<query_rootRequest[T]>
>;

export type MutationArguments<T extends keyof mutation_rootRequest> = SecondElement<
	NonNullable<mutation_rootRequest[T]>
>;
export type MutationInput<T extends keyof mutation_rootRequest> = FirstElement<
	NonNullable<mutation_rootRequest[T]>
>;

export type SubscriptionArguments<T extends keyof subscription_rootRequest> = SecondElement<
	NonNullable<subscription_rootRequest[T]>
>;
export type SubscriptionInput<T extends keyof subscription_rootRequest> = FirstElement<
	NonNullable<subscription_rootRequest[T]>
>;

export type OpRequest = {
	kind: 'query' | 'mutation';
	alias: string;
	query: string;
	variables: Record<string, unknown>;
	declarations: string[];
	dedup_key: string;
};

export type SubscriptionRequest = {
	query: string;
	variables: Record<string, unknown>;
};

let alias_counter = 0;

function stable_stringify(value: unknown): string {
	if (value === null || typeof value !== 'object') return JSON.stringify(value);
	if (Array.isArray(value)) return '[' + value.map(stable_stringify).join(',') + ']';
	const keys = Object.keys(value as Record<string, unknown>).sort();
	return (
		'{' +
		keys
			.map((key) => JSON.stringify(key) + ':' + stable_stringify((value as Record<string, unknown>)[key]))
			.join(',') +
		'}'
	);
}

/**
 * Renames `$name` → `$alias_name` in a query body WITHOUT touching occurrences
 * inside string literals (T5): a literal like `"literal $v1 text"` must survive
 * when a real `$v1` variable is also being renamed. Handles `"..."` and
 * `"""..."""` block strings.
 */
export function rename_variable_refs(body: string, name: string, alias: string): string {
	const search = '$' + name;
	const replacement = '$' + alias + '_' + name;
	let out = '';
	let in_string = false;
	let i = 0;
	while (i < body.length) {
		const ch = body[i];
		if (ch === '"') {
			if (body.startsWith('"""', i)) {
				const end = body.indexOf('"""', i + 3);
				if (end === -1) {
					out += body.slice(i);
					break;
				}
				out += body.slice(i, end + 3);
				i = end + 3;
				continue;
			}
			in_string = !in_string;
			out += ch;
			i += 1;
			continue;
		}
		if (!in_string && body.startsWith(search, i)) {
			const next = body[i + search.length];
			if (!next || !/[A-Za-z0-9_]/.test(next)) {
				out += replacement;
				i += search.length;
				continue;
			}
		}
		out += ch;
		i += 1;
	}
	return out;
}

function build_op(
	kind: 'query' | 'mutation',
	generated: { query: string; variables: Record<string, unknown> }
): OpRequest {
	alias_counter += 1;
	const alias = 'a' + alias_counter;

	const header = generated.query.match(/^(query|mutation)\s*(?:\(([\s\S]*?)\))?\s*/);
	const header_text = header?.[0] ?? '';
	const declaration_text = header?.[2] ?? '';

	let body = generated.query.slice(header_text.length).trim();
	if (body.endsWith('}')) body = body.slice(0, -1).trim();
	if (body.startsWith('{')) body = body.slice(1).trim();

	const declarations: string[] = [];
	if (declaration_text) {
		// NOTE (H1): declarations are split on ',' — a default value containing
		// a comma (e.g. `$v1: my_input = {a: 1, b: 2}`) would be split. No
		// generated query emits defaults today; revisit if one does.
		for (const declaration of declaration_text.split(',')) {
			const colon_index = declaration.indexOf(':');
			const name = declaration.slice(0, colon_index).trim().replace(/^\$/, '');
			declarations.push(
				'$' + alias + '_' + name + ':' + declaration.slice(colon_index + 1).trim()
			);
		}
	}

	const variables: Record<string, unknown> = {};
	for (const name of Object.keys(generated.variables)) {
		variables[alias + '_' + name] = generated.variables[name];
		body = rename_variable_refs(body, name, alias);
	}

	return {
		kind,
		alias,
		query: `${alias}: ${body}`,
		variables,
		declarations,
		dedup_key: kind + '|' + generated.query + '|' + stable_stringify(generated.variables)
	};
}

export const ops = {
	query_one<T extends keyof query_rootRequest>(
		collection: T,
		input: QueryOptions<T>,
		return_fields: QueryArguments<T>
	): OpRequest {
		return build_op('query', generateQueryOp({ [collection]: [input, return_fields] }));
	},

	query_many<T extends keyof query_rootRequest>(
		collection: T,
		input: QueryOptions<T> | undefined,
		return_fields: QueryArguments<T>
	): OpRequest {
		const generated = generateQueryOp({
			[collection]: input
				? [{ ...(input as object) }, { ...(return_fields as object) }]
				: return_fields
		});
		return build_op('query', generated);
	},

	mutate<T extends keyof mutation_rootRequest>(
		collection: T,
		input: MutationInput<T>,
		return_fields: MutationArguments<T>
	): OpRequest {
		return build_op('mutation', generateMutationOp({ [collection]: [input, return_fields] }));
	},

	delete_one<T extends keyof mutation_rootRequest>(
		collection: T,
		id: string
	): OpRequest {
		return build_op('mutation', generateMutationOp({ [collection]: [{ id }, { id: true }] }));
	},

	subscribe<T extends keyof subscription_rootRequest>(
		collection: T,
		input: SubscriptionInput<T>,
		return_fields: SubscriptionArguments<T>
	): SubscriptionRequest {
		const { query, variables } = generateSubscriptionOp({ [collection]: [input, return_fields] });
		return { query, variables };
	}
};

export type QueryResult<K extends keyof query_root> = query_root[K] | null;
export type MutationResult<K extends keyof mutation_root> = mutation_root[K] | null;
export type SubscriptionResult<K extends keyof subscription_root> = subscription_root[K] | null;
